import React, { useContext, useState } from "react";
import { Field, Formik } from "formik";
import { get } from "lodash";
import { Row, Col, Form, Modal, Button } from "react-bootstrap";
import {
  faTimes,
  faExclamationTriangle,
} from "@fortawesome/pro-light-svg-icons";
import { faPencilAlt } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Yup from "yup";
import { ManageCompanyLayout, FieldButtonGroup } from "../../components";
import {
  manageCompanyStore,
  saveCompanyDetails,
  setManageCompanyToastInfo,
} from "../../contexts";
import { useRouterParams } from "../../abstracts";
import {
  ROUTES,
  MANAGE_COMPANY_ROUTES,
  manageCompanyFormNames,
  formFields,
  FLOW_TYPES,
  yesNoOptions,
  getAdvancedPathWithParam,
  getFlowBasedFormValues,
  returnOnlyIfBoolean,
  clearFieldValues,
} from "../../utils";

const modeOfHoursOptions = [
  {
    label: "Pay Period",
    value: 1,
  },
  {
    label: "Year To Date",
    value: 2,
  },
];

const modeOfCompensationOptions = [...modeOfHoursOptions];
const modeOfContributionOptions = [...modeOfHoursOptions];

const schema = Yup.object().shape({});

const CompanySettingsContainer = (props) => {
  const { history } = props;
  const { state, dispatch } = useContext(manageCompanyStore);
  const { companyId, flow } = useRouterParams();
  const formName = manageCompanyFormNames.SETTINGS_MANAGE_COMPANY;
  const fields = formFields[formName];
  const [isModalOpen, setIsModalOpen] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const onFormSubmit = (values) => {
    saveCompanyDetails(
      {
        ...values,
        logo: get(state, "logo")
              ? get(state, "logo")
              : get(state, "api.data.logo"),
      },
      dispatch,
      state
    ).then((response) => {
      if (!response.isSuccessful) {
        var x = response.errorMessages;
        if (
          x[0].controlName == null &&
          x[0].errorCode !== null &&
          x[0].message !== null
        ) {
          setErrorMsg(`${x[0].errorCode} : ${x[0].message}`);
          setIsModalOpen(true);
        }
      } else {
        dispatch(
          setManageCompanyToastInfo({
            showToast: true,
            toastMessage: "Data saved successfully",
          })
        );
        history.push(
          getAdvancedPathWithParam({
            path: MANAGE_COMPANY_ROUTES.MANAGE_PAYROLL_FREQUENCY,
            pathParam: [flow, companyId],
          })
        );
      }
    });
  };

  const buttons = [
    {
      link: ROUTES.COMPANY,
      label: "Cancel",
      variant: "secondary",
      type: "button",
      flow: [FLOW_TYPES.ADD, FLOW_TYPES.SAVE],
    },
    {
      label: "Save",
      variant: "primary",
      type: "submit",
      flow: [FLOW_TYPES.SAVE, FLOW_TYPES.ADD],
    },
    {
      label: "Cancel",
      variant: "secondary",
      type: "button",
      flow: [FLOW_TYPES.EDIT],
      link: ROUTES.COMPANY,
    },
    {
      label: "Edit",
      variant: "primary",
      type: "button",
      flow: [FLOW_TYPES.EDIT],
      onClick: () =>
        history.push(
          getAdvancedPathWithParam({
            path: MANAGE_COMPANY_ROUTES.SETTINGS,
            pathParam: [FLOW_TYPES.SAVE, companyId],
          })
        ),
    },
  ];
  const isEdit = flow === FLOW_TYPES.EDIT;
  const isSave = flow === FLOW_TYPES.SAVE;
  const formValues = getFlowBasedFormValues(get(state, formName, {}));
  return (
    <Formik
      initialValues={{
        [fields.modeOfHours]: get(formValues, fields.modeOfHours) || 1,
        [fields.modeOfCompensation]:
          get(formValues, fields.modeOfCompensation) || 1,
        [fields.isPayrollCalenderRequire]:
          get(formValues, fields.isPayrollCalenderRequire) || false,
        [fields.isLatePayrollApplicable]:
          get(formValues, fields.isLatePayrollApplicable) || false,
        [fields.isFileGenerationRequire]:
          get(formValues, fields.isFileGenerationRequire) || false,
        [fields.isFileGenerationRequire]: true,
        [fields.modeOfContribution]:
          get(formValues, fields.modeOfContribution) || 1,
        [fields.isPayrollConfirmationApplicable]:
          get(formValues, fields.isPayrollConfirmationApplicable) || false,
      }}
      validationSchema={schema}
      onSubmit={onFormSubmit}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({ handleChange, setFieldValue, handleSubmit, values, ...rest }) => {
        return (
          <Form
            autoComplete="off"
            className="h-100"
            onSubmit={handleSubmit}
            validated={rest.submitCount > 0}
          >
            <ManageCompanyLayout buttons={buttons} pageFlow={flow}>
              <Row>
                <Col>
                  <Field
                    isRequired
                    name={fields.modeOfHours}
                    label="Mode of hours submission"
                    size="smdd"
                    options={modeOfHoursOptions}
                    selectedValue={values[fields.modeOfHours]}
                    onChange={(value) => {
                      setFieldValue(fields.modeOfHours, value);
                    }}
                    disabled={isEdit && !isSave}
                    component={FieldButtonGroup}
                  />

                  <Field
                    isRequired
                    name={fields.modeOfCompensation}
                    label="Mode of compensation"
                    size="smdd"
                    options={modeOfCompensationOptions}
                    selectedValue={values[fields.modeOfCompensation]}
                    onChange={(value) => {
                      setFieldValue(fields.modeOfCompensation, value);
                    }}
                    disabled={isEdit && !isSave}
                    component={FieldButtonGroup}
                  />
                  <Field
                    isRequired
                    name={fields.modeOfContribution}
                    label="Mode of contribution submission"
                    size="smdd"
                    options={modeOfContributionOptions}
                    selectedValue={values[fields.modeOfContribution]}
                    onChange={(value) => {
                      setFieldValue(fields.modeOfContribution, value);
                    }}
                    disabled={isEdit && !isSave}
                    component={FieldButtonGroup}
                  />

                  <Field
                    isRequired
                    name={fields.isPayrollCalenderRequire}
                    label="Payroll Calendar Required?"
                    size="sm"
                    options={yesNoOptions}
                    selectedValue={returnOnlyIfBoolean(
                      values[fields.isPayrollCalenderRequire]
                    )}
                    onChange={(value) => {
                      setFieldValue(fields.isPayrollCalenderRequire, value);
                      setFieldValue(fields.isLatePayrollApplicable, false);
                    }}
                    disabled={isEdit && !isSave}
                    component={FieldButtonGroup}
                  />
                  {values[[fields.isPayrollCalenderRequire]] && (
                    <Field
                      isRequired
                      name={fields.isLatePayrollApplicable}
                      label="Late payroll notifications applicable?"
                      size="sm"
                      options={yesNoOptions}
                      selectedValue={returnOnlyIfBoolean(
                        values[fields.isLatePayrollApplicable]
                      )}
                      onChange={(value) => {
                        setFieldValue(fields.isLatePayrollApplicable, value);
                      }}
                      disabled={isEdit && !isSave}
                      component={FieldButtonGroup}
                    />
                  )}
                  {
                    <Field
                      isRequired
                      name={fields.isPayrollConfirmationApplicable}
                      label="Payroll confirmation email applicable?"
                      size="sm"
                      options={yesNoOptions}
                      selectedValue={returnOnlyIfBoolean(
                        values[fields.isPayrollConfirmationApplicable]
                      )}
                      onChange={(value) => {
                        setFieldValue(
                          fields.isPayrollConfirmationApplicable,
                          value
                        );
                      }}
                      disabled={isEdit && !isSave}
                      component={FieldButtonGroup}
                    />
                  }
                  {/* <Field
                    isRequired
                    name={fields.isFileGenerationRequire}
                    label="Census and payroll file generation required?"
                    size="sm"
                    options={yesNoOptions}
                    selectedValue={returnOnlyIfBoolean(
                      values[fields.isFileGenerationRequire]
                    )}
                    onChange={(value) => {
                      setFieldValue(fields.isFileGenerationRequire, value);
                    }}
                    disabled={isEdit && !isSave}
                    component={FieldButtonGroup}
                  /> */}
                </Col>
              </Row>
              <Modal show={isModalOpen} onHide={handleClose}>
                <Modal.Body style={{ borderTop: "5px solid #f94f50" }}>
                  <div className="text-right">
                    <FontAwesomeIcon
                      icon={faTimes}
                      color="#000"
                      onClick={handleClose}
                    />
                  </div>
                  <div className="d-flex">
                    <div className="pd-15">
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        color="#f94f50"
                        size="3x"
                      />
                    </div>
                    <div className="remove-text">
                      <p>{errorMsg}</p>
                      <br />
                      <Button className="cancel-btn" onClick={handleClose}>
                        Ok
                      </Button>
                    </div>
                  </div>
                </Modal.Body>
              </Modal>
            </ManageCompanyLayout>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CompanySettingsContainer;
